import React, { useRef, useState } from "react";
import { Download, Upload, ShieldCheck, DatabaseBackup, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BackupRestore() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.role === "super_admin";

  const downloadBackup = async () => {
    setLoading(true);
    try {
      const res = await api.get('/backup/export', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = isAdmin ? `awebbee-gmb-full-backup-${date}.json` : `awebbee-gmb-client-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch (err) {
      toast.error(apiError(err, 'Backup download failed'));
    } finally {
      setLoading(false);
    }
  };

  const restoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!window.confirm('Restore this backup? Existing data will be preserved; matching records will be updated.')) {
      event.target.value = '';
      return;
    }
    setLoading(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await api.post('/backup/import', json);
      toast.success('Backup restored successfully');
    } catch (err) {
      toast.error(apiError(err, 'Restore failed. Please upload a valid backup JSON file.'));
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-overline">Data protection</p>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Backup & Restore</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          Download a JSON backup before redeploying or making major changes. Your app data is stored in MongoDB Atlas, so normal code redeploys will not delete data. This tool gives you an extra manual safety backup.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><DatabaseBackup className="h-5 w-5 text-brand-700" /> Backup actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <h3 className="font-semibold text-slate-900">Download backup</h3>
              <p className="mt-1 text-sm text-slate-500">
                {isAdmin ? 'Super Admin backup includes all clients, users, settings, posts, reviews, reports, and plans.' : 'Client backup includes only your business data, users, settings, posts, reviews, and reports.'}
              </p>
              <Button onClick={downloadBackup} disabled={loading} className="mt-4 rounded-xl bg-brand-700 text-white hover:bg-brand-800">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download Backup
              </Button>
            </div>

            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <h3 className="font-semibold text-slate-900">Restore backup</h3>
              <p className="mt-1 text-sm text-slate-600">
                Restore is non-destructive: it updates matching records and adds missing records. It does not wipe your current database.
              </p>
              <input ref={fileRef} type="file" accept="application/json,.json" onChange={restoreBackup} className="hidden" />
              <Button onClick={() => fileRef.current?.click()} disabled={loading} variant="outline" className="mt-4 rounded-xl">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload & Restore JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-green-600" /> Safe redeploy rules</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <p>1. Keep the same MongoDB Atlas <b>MONGO_URI</b>.</p>
            <p>2. Do not delete or recreate the Atlas database.</p>
            <p>3. Download backup before uploading a new ZIP.</p>
            <p>4. This build has demo/sample data disabled.</p>
            <p>5. Real clients should be created only from Super Admin.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
